import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import CIMSymbol from "@arcgis/core/symbols/CIMSymbol.js";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils.js";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import { AppConfig } from "../../../AppConfig";
import { getMapView } from "../../../utils/arcgisUtils";
import { clamp } from "../../../utils/helpers";
import {
    DefaultSectorValues,
    LighthouseData,
    SectorColor,
    SectorFallbackColor,
} from "../../../utils/lighthouseConstants";
import { applySectorOrder, getSectorNumbers } from "../../../utils/sectorRules";
import { NFSStatus } from "../../../utils/nfsUtils";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import type { CIMSolidStroke, CIMLineSymbol, CIMSolidFill, CIMPolygonSymbol } from "@arcgis/core/symbols/cim/types";
import type Symbol from "@arcgis/core/symbols/Symbol";

interface DrawSectorsParams {
    graphicsLayer: GraphicsLayer;
    data: LighthouseData;
    setIsEditing: ((value: boolean) => void) | undefined;
    sectorsToFill?: number[];
    opacity?: number;
    drawNumbers?: boolean;
    trueNorthCorrection?: number;
    overrideSectorWidth?: boolean;
    centerFeature?: Graphic | null | undefined;
    removeExistingGraphics?: boolean;
    /** Snapshot of the numbering order (globalids) the numbers follow, see applySectorOrder */
    sectorOrder?: string[] | null;
}

export const drawSectors = async ({
    graphicsLayer,
    data,
    setIsEditing,
    sectorsToFill,
    opacity,
    drawNumbers,
    trueNorthCorrection = 0,
    overrideSectorWidth = false,
    centerFeature,
    sectorOrder,
}: DrawSectorsParams) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    graphicsLayer.removeAll();

    setIsEditing?.(true);

    // Fetch the sector line symbology
    const sectorLineLayer = mapview.map.findLayerById("Sektorlinje");
    const dummyLineGraphic = new Graphic({ layer: sectorLineLayer });
    const sectorLineSymbol = symbolUtils.getDisplayedSymbol(dummyLineGraphic) as Promise<CIMSymbol>;
    const fallbackLineSymbol = new CIMSymbol({
        data: {
            type: "CIMSymbolReference",
            symbol: {
                type: "CIMLineSymbol",
                symbolLayers: [
                    {
                        type: "CIMSolidStroke",
                        capStyle: "Butt",
                        color: [0, 0, 0, 255],
                        enable: true,
                        joinStyle: "Round",
                        width: 0.2,
                    },
                ],
            },
        },
    });

    // Fetch the sector fill symbology
    const sectorColorSymbology = mapview.map.findLayerById("Sektorflate");
    const dummySectorRed = new Graphic({
        layer: sectorColorSymbology,
        attributes: { lys_farge: Number.parseInt(SectorColor.RED) },
    });
    const dummySectorGreen = new Graphic({
        layer: sectorColorSymbology,
        attributes: { lys_farge: Number.parseInt(SectorColor.GREEN) },
    });
    const dummySectorYellow = new Graphic({
        layer: sectorColorSymbology,
        attributes: { lys_farge: Number.parseInt(SectorColor.WHITE) },
    });
    const sectorRedSymbol = symbolUtils.getDisplayedSymbol(dummySectorRed);
    const sectorGreenSymbol = symbolUtils.getDisplayedSymbol(dummySectorGreen);
    const sectorYellowSymbol = symbolUtils.getDisplayedSymbol(dummySectorYellow);

    const sectorOutline = {
        color: [0, 0, 0, 0],
        width: 1,
    };

    const sectorDarkSymbol = new SimpleFillSymbol({
        color: SectorFallbackColor.DARK,
        outline: sectorOutline,
    });
    const fallbackRedSymbol = new SimpleFillSymbol({
        color: SectorFallbackColor.RED,
        outline: sectorOutline,
    });
    const fallbackGreenSymbol = new SimpleFillSymbol({
        color: SectorFallbackColor.GREEN,
        outline: sectorOutline,
    });
    const fallbackYellowSymbol = new SimpleFillSymbol({
        color: SectorFallbackColor.WHITE,
        outline: sectorOutline,
    });

    let previousEndAngle = 0;
    const firstAngle = data.sectors[0].startAngle ?? 0;

    const sectorNumbers = getSectorNumbers(applySectorOrder(data, sectorOrder));

    for (const [index, sector] of data.sectors.entries()) {
        const startAngle = sector.startAngle ?? previousEndAngle;
        const endAngle =
            index < data.sectors.length - 1 ? data.sectors[index + 1].startAngle ?? 360 + firstAngle : 360 + firstAngle;
        const innerRadius = sector.innerRadius ?? DefaultSectorValues.defaultInnerRadius;
        const sectorThickness = AppConfig.Lighthouse.AdaptiveSectorThickness
            ? clamp(
                  mapview.viewpoint.scale / 50,
                  AppConfig.Lighthouse.MinSectorThickness ?? AppConfig.Lighthouse.DefaultSectorThickness,
                  AppConfig.Lighthouse.MaxSectorThickness ?? innerRadius
              )
            : AppConfig.Lighthouse.DefaultSectorThickness;
        const outerRadius =
            innerRadius + (overrideSectorWidth ? AppConfig.Lighthouse.DefaultSectorThickness : sectorThickness); // in meters

        let sectorSymbol;
        let sectorFallbackSymbol;

        switch (sector.color) {
            case SectorColor.RED:
                sectorSymbol = sectorRedSymbol;
                sectorFallbackSymbol = fallbackRedSymbol;
                break;
            case SectorColor.GREEN:
                sectorSymbol = sectorGreenSymbol;
                sectorFallbackSymbol = fallbackGreenSymbol;
                break;
            case SectorColor.WHITE:
                sectorSymbol = sectorYellowSymbol;
                sectorFallbackSymbol = fallbackYellowSymbol;
                break;
            case SectorColor.DARK:
            default:
                sectorSymbol = Promise.resolve(sectorDarkSymbol);
                sectorFallbackSymbol = sectorDarkSymbol;
                break;
        }

        const lineEndIndex = index < data.sectors.length - 1 ? index + 1 : 0;
        const startLineLength = sector.startLineLength ?? DefaultSectorValues.defaultLineLength;
        const endLineLength =
            sector.endLineLength ?? data.sectors[lineEndIndex].startLineLength ?? DefaultSectorValues.defaultLineLength;

        if (sector.color) {
            const sectorGraphic = drawSingleSector({
                center: data.center,
                innerRadius: innerRadius,
                outerRadius: outerRadius,
                startAngle: startAngle,
                endAngle: endAngle,
                startLineLength: startLineLength,
                endLineLength: endLineLength,
                symbol: sectorSymbol,
                fallbackSymbol: sectorFallbackSymbol,
                index: index,
                lighthouseId: data.id,
                globalid: sector.globalid,
                trueNorthCorrection: trueNorthCorrection,
                fill: sectorsToFill?.includes(index),
                opacity: sector.color === SectorColor.DARK ? 0 : opacity,
                nfs_status: sector.nfs_status ?? NFSStatus.UNDEFINED,
            });
            sectorGraphic.then((sectorGraphic) => {
                if (
                    !graphicsLayer.graphics.some(
                        (graphic) => graphic.attributes.objectType === "sector" && graphic.attributes.index === index
                    )
                ) {
                    graphicsLayer.add(sectorGraphic);
                }
            });
        }

        if (drawNumbers && sector.color !== SectorColor.DARK) {
            drawText({
                id: data.id,
                graphicsLayer,
                center: data.center,
                outerRadius,
                startAngle,
                endAngle,
                trueNorthCorrection,
                index,
                displayIndex: (sectorNumbers.get(sector.globalid) ?? index + 1) - 1,
            });
        }

        previousEndAngle = endAngle;
    }

    if (data.sectors.length > 1) {
        // Draw all lines after, i.e. on top of, sectors
        for (const [index, sector] of data.sectors.entries()) {
            const startLineLength = sector.startLineLength ?? DefaultSectorValues.defaultLineLength;

            // Draw lines between sectors
            if (index < data.sectors.length) {
                const lineStartAngle = data.sectors[index].startAngle ?? 0;
                const lineGraphicStart = drawSingleLine({
                    center: data.center,
                    angle: lineStartAngle,
                    length: startLineLength,
                    index: index,
                    lighthouseId: data.id,
                    sectorId: sector.globalid,
                    sectorLineSymbol: sectorLineSymbol,
                    fallbackSymbol: fallbackLineSymbol,
                    trueNorthCorrection: trueNorthCorrection,
                    nfs_status: sector.nfs_status ?? NFSStatus.UNDEFINED,
                });

                lineGraphicStart.then((lineGraphic) => {
                    if (
                        !graphicsLayer.graphics.some(
                            (graphic) => graphic.attributes.objectType === "line" && graphic.attributes.index === index
                        )
                    ) {
                        graphicsLayer.add(lineGraphic);
                    }
                });
            }
        }
    }

    // Fetch the lighthouse center symbology
    if (!centerFeature) {
        const centerLayer = mapview.map.findLayerById("Lys");
        centerFeature = new Graphic({ layer: centerLayer, attributes: { klasse: 1, lys_farge: 0 } });
    }

    const centerSymbol = symbolUtils.getDisplayedSymbol(centerFeature) as Promise<SimpleMarkerSymbol>;
    const fallbackCenterSymbol = new SimpleMarkerSymbol({
        style: "circle",
        color: [0, 0, 0, 1],
        size: 8,
    });

    const centerGraphic = await drawCenter(data.center, centerSymbol, fallbackCenterSymbol, data.id);

    if (!graphicsLayer.graphics.some((graphic) => graphic.attributes.objectType === "center")) {
        graphicsLayer.add(centerGraphic);
    }

    setIsEditing?.(false);
};

interface DrawTextParams {
    id: string;
    graphicsLayer: GraphicsLayer;
    center: Point;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    trueNorthCorrection: number;
    index: number;
    displayIndex: number;
}

const drawText = async ({
    id,
    graphicsLayer,
    center,
    outerRadius,
    startAngle,
    endAngle,
    trueNorthCorrection,
    index,
    displayIndex,
}: DrawTextParams) => {
    const mapView = getMapView();
    if (!mapView?.map) return;

    const labelPoint = new Point({
        x:
            center.x +
            (outerRadius + 60) *
                -Math.cos((-(startAngle - trueNorthCorrection + (endAngle - startAngle) / 2 + 270) * Math.PI) / 180),
        y:
            center.y +
            (outerRadius + 60) *
                -Math.sin((-(startAngle - trueNorthCorrection + (endAngle - startAngle) / 2 + 270) * Math.PI) / 180),
        spatialReference: center.spatialReference,
    });

    const textGraphic = new Graphic({
        geometry: labelPoint,
        symbol: new TextSymbol({
            verticalAlignment: "middle",
            color: [0, 0, 0, 1],
            haloColor: [255, 255, 255, 1],
            haloSize: 1,
            text: (displayIndex + 1).toString(),
            xoffset: 0,
            yoffset: 0,
            font: {
                size: 12,
                family: "sans-serif",
            },
        }),
        attributes: {
            objectType: "text",
            index: index,
            lighthouseId: id,
        },
    });

    graphicsLayer.add(textGraphic);
};

const drawCenter = async (
    center: Point,
    symbol: Promise<SimpleMarkerSymbol>,
    fallbackSymbol: SimpleMarkerSymbol,
    id: string
) => {
    const centerGraphic = new Graphic({
        geometry: center,
        symbol: (await symbol) ?? fallbackSymbol,
        attributes: {
            objectType: "center",
            lighthouseId: id,
        },
    });

    return centerGraphic;
};

interface SectorParams {
    center: Point;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    startLineLength: number;
    endLineLength: number;
    symbol: Promise<Symbol | null | undefined>;
    fallbackSymbol: SimpleFillSymbol;
    index: number;
    lighthouseId: string;
    globalid: string;
    trueNorthCorrection: number;
    fill?: boolean;
    opacity?: number;
    nfs_status?: number;
}

const drawSingleSector = async ({
    center,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    startLineLength,
    endLineLength,
    symbol,
    fallbackSymbol,
    index,
    lighthouseId,
    globalid,
    trueNorthCorrection,
    fill,
    opacity,
    nfs_status,
}: SectorParams) => {
    const points = [];
    const numPoints = Math.min(2000, Math.ceil((endAngle - startAngle) / 5));
    const angleStep = (endAngle - startAngle) / numPoints;
    const rotationOffset = 270;

    // Outer arc
    for (let i = 0; i <= numPoints; i++) {
        const angle = startAngle + i * angleStep + rotationOffset - trueNorthCorrection;
        const finalOuterRadius = fill ? Math.max(startLineLength, endLineLength) : outerRadius;
        const x = center.x + finalOuterRadius * -Math.cos(-(angle * Math.PI) / 180);
        const y = center.y + finalOuterRadius * -Math.sin(-(angle * Math.PI) / 180);

        points.push([x, y]);
    }

    // Inner arc (reverse direction)
    for (let i = numPoints; i >= 0; i--) {
        const angle = startAngle + i * angleStep + rotationOffset - trueNorthCorrection;
        const x = center.x + innerRadius * -Math.cos(-(angle * Math.PI) / 180);
        const y = center.y + innerRadius * -Math.sin(-(angle * Math.PI) / 180);
        points.push([x, y]);
    }

    const polygon = new Polygon({
        rings: [points],
        spatialReference: center.spatialReference,
    });

    const sectorGraphic = new Graphic({
        geometry: polygon,
        symbol: ((await symbol) as SimpleFillSymbol) ?? fallbackSymbol,
        attributes: {
            objectType: "sector",
            index: index,
            lighthouseId: lighthouseId,
            globalid: globalid,
            nfs_status: nfs_status,
        },
    });

    if ((sectorGraphic?.symbol as CIMSymbol)?.data) {
        for (const layer of ((sectorGraphic?.symbol as CIMSymbol)?.data?.symbol as CIMPolygonSymbol)
            ?.symbolLayers ?? []) {
            (layer as CIMSolidFill).color[3] = (opacity ?? 0.5) * 255;
        }
    } else {
        (sectorGraphic.symbol as SimpleFillSymbol).color.a = opacity ?? 0.5;
    }

    return sectorGraphic;
};

interface LineParams {
    center: Point;
    angle: number;
    length: number;
    index: number;
    lighthouseId: string;
    sectorId: string;
    sectorLineSymbol: Promise<CIMSymbol | SimpleLineSymbol | null | undefined>;
    fallbackSymbol: CIMSymbol;
    trueNorthCorrection: number;
    nfs_status: number;
}

const drawSingleLine = async ({
    center,
    angle,
    length,
    index,
    lighthouseId,
    sectorId,
    sectorLineSymbol,
    fallbackSymbol,
    trueNorthCorrection,
    nfs_status,
}: LineParams) => {
    const finalAngle = angle + 270 - trueNorthCorrection;
    const x = center.x + length * -Math.cos(-(finalAngle * Math.PI) / 180);
    const y = center.y + length * -Math.sin(-(finalAngle * Math.PI) / 180);

    const polyline = new Polyline({
        paths: [
            [
                [center.x, center.y],
                [x, y],
            ],
        ],
        spatialReference: center.spatialReference,
    });

    const symbol = (await sectorLineSymbol) ?? fallbackSymbol;

    if ((symbol as CIMSymbol).data) {
        for (const layer of ((symbol as CIMSymbol)?.data?.symbol as CIMLineSymbol)?.symbolLayers ?? []) {
            (layer as CIMSolidStroke).width = 2;
        }
    } else {
        (symbol as SimpleLineSymbol).width = 2;
    }

    const lineGraphic = new Graphic({
        geometry: polyline,
        symbol: symbol,
        attributes: {
            objectType: "line",
            index: index,
            lighthouseId: lighthouseId,
            sectorId: sectorId,
            nfs_status: nfs_status,
        },
    });

    return lineGraphic;
};
